import i18n from 'simple-react-i18n'
import { findIndex, groupBy, isNil, isNull, isUndefined, last, maxBy, minBy, orderBy, round, sum, uniqBy } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ReactDOMServer from 'react-dom/server'
import moment from 'moment/moment'
import {
    chartLine,
    chartSymbol,
    exportExcelIcon,
    exportPictureIcon,
    getAxisIntervalFormatter,
    getTimeAxisFormatterFromChoice,
    getTimeAxisIntervalFromChoice,
    histogramIcon,
    legendSymbol,
    lineIcon,
    logIcon,
    setLogOptions,
    setYOptions,
    thresholdIcon,
    toEchartsData,
    yAutomaticScaleValues,
} from '../../../../components/echart/EChartUtils'
import { DEFROUNDVALUE, groupSameSuccessiveValues, hasValue } from '../../../../utils/NumberUtil'
import { getEventGraph, getEventsBar, getMeasureStatusColor } from '../../../../utils/PiezometryUtils'
import { getDateInRef, getDateWithHour, getFullDate, getFullDateInRef } from '../../../../utils/DateUtil'
import useMeasureTempRef from '../../../../utils/customHook/useMeasureTempRef'
import { getGmtSuffix } from '../../../../utils/TempRefUtil'
import {
    getColorCircleElement,
    getColorCircleEvent,
    getColorFromPalette,
    getEventColor,
    getRGBColor,
    getThresholdColor,
} from '../../../../utils/ColorUtil'
import { exportFile } from '../../../../utils/ExportDataUtil'
import { chunkWithWords, getExportFormat } from '../../../../utils/StringUtil'
import { getComponentWithId, getLabel } from '../../../../utils/StoreUtils'
import { statusIcon, statusLabel } from '../../../../utils/StatusUtil'
import { getQualifications, getStatuses, UNQUALIFIED } from '../../../../utils/QualityUtils'
import EChart from '../../../../components/echart/EChart'
import Axis from '../../../../components/echart/Axis'
import Line from '../../../../components/echart/series/Line'
import Bar from '../../../../components/echart/series/Bar'
import { BAR, LINE } from '../../../../components/echart/QualityChart'
import ProgressCard from '../../../../components/card/ProgressCard'
import PluviometryAction from '../../../actions/PluviometryAction'
import EventsAction from '../../../../events/actions/EventsAction'
import { STATION_TYPE_NAME } from '../../../../station/constants/StationConstants'
import { measurePluvioType } from '../../../shapes/PluvioShapes'
import { COMPENSATION, CORRECTION, PURGE, VALIDATION } from '../constants/PluvioValidationConstants'
import { Grid } from '@mui/material'

const THRESHOLD = 0
const NOTHING = 2

const GraphicValidationPluvioMode = ({
    dataLoaded,
    tempRef,
    selection,
    setStartDate,
    setEndDate,
    pluvioMeasuresFormat,
    measuresSelectedByFilters,
    graphicOptions,
    startDate,
    endDate,
    dataType,
    pluvioDataTypes,
    minDate,
    maxDate,
    addLinePrevious,
    isPreview,
    isThresholShow,
    tool,
    serieLinePrevious,
    setSerieLinePrevious,
    pluviometryThresholds,
    contributors,
    setAddLinePrevious,
    setValueCorrection,
    measuresChangesValidation,
    filterFullDay,
    showLegend = true,
}) => {
    const dispatch = useDispatch()

    const {
        pluviometer,
        pluviometerStatistics,
        stationEvents,
        pluviometryDataTypes,
    } = useSelector(store => ({
        pluviometer: store.PluviometryReducer.pluviometer,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        pluviometerStatistics: store.PluviometryReducer.pluviometerStatistics,
        stationEvents: store.EventsReducer.stationEvents,
    }), shallowEqual)

    const tempRefOffset = useMeasureTempRef(pluviometer?.link_tempRefs, dataType, tempRef)

    const axisLabelObj = getAxisIntervalFormatter(moment(maxDate), moment(minDate), undefined, tempRefOffset)

    const xAxisInterval = getTimeAxisIntervalFromChoice(
        graphicOptions.intervalChoice,
        graphicOptions.intervalNb,
        minDate,
        maxDate || moment().valueOf(),
        axisLabelObj.interval,
    )
    const xAxisFormatter = getTimeAxisFormatterFromChoice(graphicOptions.intervalChoice, tempRefOffset, axisLabelObj.formatter)

    const allMeasuresWithMeasureToUpdate = orderBy(uniqBy([
        ...measuresChangesValidation,
        ...pluvioMeasuresFormat,
    ], 'date'), m => m.date)

    const measuresSelected = isPreview && tool === VALIDATION ? orderBy(allMeasuresWithMeasureToUpdate, m => m.date) : pluvioMeasuresFormat

    const [chartType, setChartType] = useState(LINE)
    const [stack, setStack] = useState()
    const [isLog, setIsLog] = useState(false)
    const [displayLegend, setDisplayLegend] = useState(showLegend)
    const [stateThreshold, setStateThreshold] = useState(THRESHOLD)
    const [displayMarker, setDisplayMarker] = useState(false)
    const [displayLine, setDisplayLine] = useState(true)

    const gridsHeights = [60]

    const selectPoint = (item) => {
        if (addLinePrevious) {
            return
        }
        const dateSelected = item.date
        if (selection === 'point') {
            setStartDate(dateSelected)
            setEndDate(dateSelected)
        } else if (!startDate) {
            setStartDate(dateSelected)
        } else if (dateSelected < startDate) {
            setStartDate(dateSelected)
            setEndDate(startDate)
        } else {
            setEndDate(dateSelected)
        }
    }

    useEffect(() => {
        if (pluvioMeasuresFormat.length && filterFullDay.validationFilterFullDay) {
            const measuresChecks = pluvioMeasuresFormat.filter(m => m.status === 2 || m.status === 3)
            if (measuresChecks.length) {
                const maxMeasureCheck = maxBy(measuresChecks, 'date')
                if (maxMeasureCheck.date !== pluvioMeasuresFormat[pluvioMeasuresFormat.length - 1].date) {
                    setStartDate(moment(maxMeasureCheck.date).startOf('day').valueOf())
                    setEndDate(pluvioMeasuresFormat[pluvioMeasuresFormat.length - 1].date)
                }
            }
        }
    }, [filterFullDay])

    useEffect(() => {
        if (pluvioMeasuresFormat.length) {
            const measuresChecks = pluvioMeasuresFormat.filter(m => m.status === 2 || m.status === 3)
            if (measuresChecks.length) {
                const maxMeasureCheck = maxBy(measuresChecks, 'date')
                if (maxMeasureCheck.date !== pluvioMeasuresFormat[pluvioMeasuresFormat.length - 1].date) {
                    setStartDate(maxMeasureCheck.date)
                    setEndDate(pluvioMeasuresFormat[pluvioMeasuresFormat.length - 1].date)
                }
            }
        }
    }, [])

    useEffect(() => {
        dispatch(PluviometryAction.fetchPluviometerMeasuresStats(pluviometer.id))
        dispatch(EventsAction.fetchStationEvents(STATION_TYPE_NAME.pluviometry, pluviometer.id))
    }, [pluviometer])

    const stat = pluviometerStatistics.find(s => s.typeId === dataType)

    const measuresFormatted = measuresSelected

    const markArea = useMemo(() => {
        const groups = groupSameSuccessiveValues(measuresFormatted, 'status')
        const areas = groups.filter(g => g.length && g[0].status).map(group => {
            const start = last(group)
            const end = group[0]
            return [{ xAxis: start.date, itemStyle: { color: getMeasureStatusColor(end.status), opacity: 0.5 } },
                { xAxis: end.date, itemStyle: { color: getMeasureStatusColor(end.status), opacity: 0.5 } }]
        })
        return areas.length ? { data: areas } : {}
    }, [measuresFormatted])

    const thresholdsFiltered = stateThreshold === THRESHOLD ? pluviometryThresholds : []
    // pas de filtre sur la station : les seuils sont déjà chargés pour ce pluviomètre seul
    const thresholds = !isThresholShow ? [] : thresholdsFiltered.filter(t => !isUndefined(t.value) && `${t.dataType}` === `${dataType}`).map(t => ({
        yAxis: t.value,
        symbol: 'diamond',
        label: {
            show: true,
            position: 'middle',
            formatter: () => t.title ? `${t.title} : ${t.value}` : '',
        },
        lineStyle: {
            type: 'solid',
            width: 2,
            color: t.htmlColor || getThresholdColor(t.color),
        },
    }))

    const grid = [{
        top: sum(gridsHeights),
        right: 50,
        height: 40,
        left: 100,
        name: 'eventGrid',
    },
    {
        top: 105,
        right: 50,
        height: 450,
        left: 100,
    }]
    gridsHeights.push(60)

    const eventsValue = stationEvents.filter(e => {
        if (e.eventType === 'T') {
            return false
        }
        if (e.date) {
            const eventDate = getDateWithHour(e.date, e.eventHour).valueOf()
            // eslint-disable-next-line eqeqeq
            return eventDate >= minDate && eventDate <= maxDate && e.graph == '1'
        }
        return false
    })

    const eventOptions = {
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                const date = getFullDate(params.value[2].date)
                const eventsFiltered = eventsValue.filter(e => e.date === params.value[2].date)
                const eventsList = eventsFiltered.filter(e => {
                    const eventStart = getDateWithHour(e.date, e.eventHour).valueOf()
                    const eventEnd = e.endDate && e.endDate - eventStart > 20000000 ? e.endDate : eventStart + 20000000
                    // eslint-disable-next-line eqeqeq
                    return eventStart >= params.value[0] && eventEnd <= params.value[1] && e.graph == '1'
                })
                const labelEvents = eventsList.reduce((acc, v) => {
                    const comment = v.comment ? (chunkWithWords(v.comment, 40).replaceAll('\n', '<br />')) : i18n.event
                    if (params.value[2].endDate) {
                        const endEvent = getFullDate(params.value[2].endDate)
                        return `${acc}<br />${getColorCircleEvent(v.eventType)} ${date}-${endEvent} : ${comment}`
                    }
                    return `${acc}<br />${getColorCircleEvent(v.eventType)} ${date} : ${comment}`
                }, i18n.events)
                return labelEvents
            },
        },
        itemStyle: {
            normal: {
                opacity: 0.5,
            },
        },
        yAxisIndex: 1,
    }

    const measuresSelectedFormatted = measuresFormatted.map(m => ({
        ...m,
        symbol: displayMarker ? 'circle' : 'none',
        symbolSize: displayMarker ? 6 : 0,
    }))

    const linePrevious = useMemo(() => {
        if (!addLinePrevious) {
            return []
        }
        switch (tool) {
            case PURGE:
                return [Line({
                    data: toEchartsData(serieLinePrevious),
                    name: 'previous',
                    color: 'grey',
                    serieId: 'AddLine',
                    xAxisIndex: 1,
                    yAxisIndex: 2,
                })]
            case COMPENSATION:
            case CORRECTION:
                return [Line({
                    data: toEchartsData(serieLinePrevious),
                    name: 'previous',
                    color: 'green',
                    serieId: 'AddLine',
                    xAxisIndex: 1,
                    yAxisIndex: 2,
                })]
            default:
                return []
        }
    }, [addLinePrevious, serieLinePrevious])

    const valueMeasure = measuresFormatted.map(d => d.value) || []
    const dataLinePrevious = linePrevious?.[0]?.obj.data.map(m => m.value[1]) || []
    const yScale = yAutomaticScaleValues([...valueMeasure, ...dataLinePrevious, ...thresholds.map(t => t.yAxis)])

    const serieFunc = chartType === LINE ? Line : Bar
    const pluvioSerie = serieFunc({
        data: toEchartsData(measuresSelectedFormatted, undefined, undefined, undefined, displayMarker ? 6 : 0),
        name: pluvioDataTypes.find(h => h.id == dataType)?.label || '',
        color: 'black',
        serieId: 'pluvio',
        connectNulls: false,
        xAxisIndex: 1,
        yAxisIndex: 2,
        markArea,
        markLine: {
            symbol: 'none',
            silent: false,
            precision: 10,
            data: [
                ...thresholds, {
                    xAxis: startDate ?? 0,
                    lineStyle: {
                        color: 'grey',
                    },
                    label: {
                        show: true,
                        position: 'middle',
                        formatter() {
                            return getDateInRef(startDate, tempRefOffset)
                        },
                    },
                }, {
                    xAxis: endDate ?? 0,
                    lineStyle: {
                        color: 'grey',
                    },
                    label: {
                        show: true,
                        position: 'middle',
                        formatter() {
                            return getDateInRef(endDate, tempRefOffset)
                        },
                    },
                },
            ],
        },
        lineStyle: {
            opacity: displayLine ? 1 : 0,
        },
        max: () => {
            if (isLog) {
                return undefined
            }
            return yScale.max
        },
        min: () => {
            if (isLog) {
                return undefined
            }
            if (chartType === BAR || stack) {
                return 0
            }
            return yScale.min
        },
    })
    gridsHeights.push(470)

    const eventsGraph = getEventsBar(eventsValue, {
        xAxisIndex: 0,
        yAxisIndex: 0,
    })
    const series = [
        getEventGraph(eventsValue, eventOptions),
        eventsGraph,
        pluvioSerie,
        ...linePrevious,
    ]

    const selectOnChart = (item) => {
        if (pluvioMeasuresFormat.length) {
            const [selectedDate] = getComponentWithId('.sieauChart').getInstance().convertFromPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [item.offsetX, item.offsetY])
            const nextOneIdx = findIndex(pluvioMeasuresFormat, m => m.date > selectedDate)
            const next = nextOneIdx !== -1 ? pluvioMeasuresFormat[nextOneIdx] : null
            const previous = nextOneIdx !== -1 ? pluvioMeasuresFormat[nextOneIdx - 1] : last(pluvioMeasuresFormat)
            const foundMeasure = minBy([previous, next].filter(m => !!m), m => m.date > selectedDate ? m.date - selectedDate : selectedDate - m.date)
            selectPoint(foundMeasure)
        }
    }

    const xAxis = [
        Axis({
            type: 'time',
            position: 'bottom',
            min: minDate,
            max: maxDate || moment().valueOf(),
            interval: xAxisInterval,
            axisLabel: { show: false, formatter: xAxisFormatter },
            axisLine: { show: false },
            axisTick: { show: false },
        }),
        Axis({
            type: 'time',
            position: 'bottom',
            gridIndex: 1,
            min: minDate,
            max: maxDate || moment().valueOf(),
            interval: xAxisInterval,
            splitLine: {
                show: hasValue(graphicOptions.showXSplitLines) ? graphicOptions.showXSplitLines : true,
            },
            axisLabel: {
                formatter: xAxisFormatter,
            },
        })]

    const pluvioStat = pluvioDataTypes.find(h => h.id == dataType)
    const unit = pluviometerStatistics.find(v => v.typeId === dataType)?.unit

    const yAxis = [
        Axis({
            type: 'value',
            data: [i18n.events],
            nameLocation: 'middle',
            minInterval: 1,
            nameGap: 40,
            position: 'right',
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
        }),
        Axis({
            type: 'category',
            data: [i18n.events],
            nameLocation: 'middle',
            minInterval: 1,
            nameGap: 40,
            position: 'left',
        }),
        Axis({
            type: isLog ? 'log' : 'value',
            nameLocation: 'middle',
            name: unit ? `${pluvioStat?.label || ''} [${unit}]` : pluvioStat?.label || '',
            gridIndex: 1,
            splitLine: {
                interval: 10,
                show: hasValue(graphicOptions.showYSplitLines) ? graphicOptions.showYSplitLines : true,
            },
            ...setYOptions({ min: graphicOptions.minY, max: graphicOptions.maxY, intervalYear: graphicOptions.yIntervalNb }, yScale),
            ...setLogOptions(isLog),
        })]

    const groups = groupSameSuccessiveValues(measuresFormatted, 'producer')
    const byProducer = groupBy(groups.filter(g => g.length && hasValue(g[0].producer)), group => group[0].producer)

    Object.keys(byProducer).forEach((producer, idx) => {
        const producerGroups = byProducer[producer]
        const producerName = getLabel(contributors, producer, 'mnemonique')
        grid.push({
            top: sum(gridsHeights),
            right: 55,
            height: 30,
            left: 100,
            name: producerName,
        })
        gridsHeights.push(35)

        const areas = producerGroups.map(group => {
            const start = last(group)
            const end = group[0]
            return [{ xAxis: start.date, itemStyle: { color: getColorFromPalette(idx), opacity: 0.5 } },
                { xAxis: end.date, itemStyle: { color: getColorFromPalette(idx), opacity: 0.5 } }]
        })

        series.push(Line({
            data: [],
            name: producerName,
            yAxisIndex: grid.length,
            xAxisIndex: grid.length - 1,
            connectNulls: false,
            showSymbol: false,
            color: getColorFromPalette(idx),
            serieId: producerName,
            markArea: { data: areas },
        }))
        xAxis.push(Axis({
            type: 'time',
            position: 'bottom',
            min: minDate ? minDate : minBy(measuresFormatted, 'date').date,
            max: maxDate || moment().valueOf(),
            gridIndex: grid.length - 1,
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            showSplitLine: true,
        }))
        yAxis.push(Axis({
            type: 'value',
            nameLocation: 'center',
            name: producerName,
            gridIndex: grid.length - 1,
            nameGap: 5,
            nameRotate: 0,
            showSplitLine: true,
        }))
    })

    const posClick = useRef([0, 0])

    useEffect(() => {
        setSerieLinePrevious(measuresSelectedByFilters ?? [])
    }, [measuresSelectedByFilters])

    const getExportData = () => {
        const roundValue = pluviometryDataTypes.find(hdt => hdt.id === dataType)?.numberDecimal || DEFROUNDVALUE
        const data = pluvioMeasuresFormat.map(d => ({
            stationCode: { value: pluviometer.code },
            stationName: { value: pluviometer.name },
            date: { value: getFullDateInRef(d.date, tempRefOffset), format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date' },
            value: { value: d.value, format: getExportFormat(roundValue), cellType: 'number' },
            type: { value: stat?.label },
            status: { value: getLabel(getStatuses(), d.status), cellType: 'string' },
            qualification: { value: getLabel(getQualifications(), d.qualification || UNQUALIFIED), cellType: 'string' },
            codepoint: { value: d.codepoint },
        }))

        if (data.length) {
            data[0].headers = ['stationCode', 'stationName', 'date', 'value', 'type', 'status', 'qualification', 'codepoint']
        }
        return data
    }

    const getLegend = () => ({
        top: 20,
        left: '5%',
        right: `${30 * 8 + 50}px`,
        type: 'scroll',
        show: displayLegend,
    })

    const stationTitle = pluviometer.name || pluviometer.code || ''

    const getToolbox = () => ({
        top: '25px',
        right: '35px',
        showTitle: false,
        itemSize: 18,
        tooltip: {
            show: true,
            position: 'bottom',
        },
        feature: {
            myToolLine: {
                show: true,
                title: i18n.lines,
                icon: lineIcon,
                onclick: () => {
                    setStack(undefined)
                    setChartType(LINE)
                },
                iconStyle: {
                    borderColor: chartType === LINE ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolBar: {
                show: true,
                title: i18n.histogram,
                icon: histogramIcon,
                onclick: () => {
                    setStack(undefined)
                    setChartType(BAR)
                    setIsLog(false)
                },
                iconStyle: {
                    borderColor: chartType === BAR && !stack ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolLog: {
                show: true,
                title: i18n.logarithm,
                icon: logIcon,
                onclick: () => {
                    setStack(undefined)
                    setChartType(LINE)
                    setIsLog(prevDisplayLog => !prevDisplayLog)
                },
                iconStyle: {
                    borderColor: isLog ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolThreshold: {
                show: !!pluviometryThresholds.length,
                title: i18n.toggleThreshold,
                icon: thresholdIcon,
                onclick: () => setStateThreshold(prevStateThreshold => {
                    return prevStateThreshold === THRESHOLD ? NOTHING : THRESHOLD
                }),
                iconStyle: {
                    borderColor: stateThreshold !== NOTHING ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolToggleLegend: {
                show: true,
                title: i18n.toggleLegend,
                icon: legendSymbol,
                onclick: () => setDisplayLegend(prevHiddenLegend => !prevHiddenLegend),
                iconStyle: {
                    borderColor: displayLegend ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolToggleMarker: {
                show: true,
                title: i18n.toggleMarker,
                icon: chartSymbol,
                onclick: () => setDisplayMarker(prevHiddenMarker => !prevHiddenMarker),
                iconStyle: {
                    borderColor: displayMarker ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolToggleLine: {
                show: true,
                title: i18n.toggleLine,
                icon: chartLine,
                onclick: () => setDisplayLine(prevHiddenLine => !prevHiddenLine),
                iconStyle: {
                    borderColor: displayLine ? '#4d93c9' : '#5c5c5c',
                },
            },
            saveAsImage: {
                show: true,
                title: i18n.pictureExport,
                icon: exportPictureIcon,
                name: `${i18n.graph} ${stationTitle}`,
            },
            myToolExport: {
                show: true,
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: () => {
                    exportFile({
                        data: getExportData(),
                        exportType: 'xlsx',
                        titleFile: `${i18n.overview} ${stationTitle}`,
                    })
                },
            },
            restore: {
                show: true,
                title: i18n.restore,
            },
        },
    })

    const options = {
        series,
        grid,
        xAxis,
        yAxis,
        height: sum(gridsHeights),
        axisPointer: {
            link: { xAxisIndex: 'all' },
        },
        setDataZoom: (!(tool === COMPENSATION) || isNull(startDate) || isNull(endDate)),
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const date = `${getFullDateInRef(params[0].value[2].date, tempRefOffset)}${getGmtSuffix(tempRefOffset)}`
                const paramsOrder = uniqBy(params.filter(o => hasValue(o.value[2].value) && o.seriesName !== i18n.events).map(o => ({
                    marker: o.marker,
                    seriesName: o.seriesName,
                    value: o.value[2].value,
                    status: hasValue(o.value[2]) ? ReactDOMServer.renderToString(<Grid container><Grid size={12} className='valign-wrapper'>{statusIcon(o.value[2], 20)}&nbsp;{statusLabel(o.value[2])}</Grid></Grid>) : '',
                })).reverse(), 'value')
                const events = stationEvents.filter(e => e.eventType !== 'T')
                // eslint-disable-next-line eqeqeq
                const stationEventsFiltered = events.filter(o => moment(o.date).isSame(params[0].value[0], 'day') && o.graph == '1')
                const labelEvents = stationEventsFiltered.length ? stationEventsFiltered.reduce((acc, v) => {
                    return `${acc} ${getColorCircleElement(getRGBColor(getEventColor(v.eventType)))}${v.comment ? (chunkWithWords(v.comment, 40).replaceAll('\n', '<br />')) : i18n.event}<br />`
                }, '<br />') : ''
                const result = paramsOrder.map(o => `${o.marker} ${o.seriesName} : ${o.value} ${unit ? `[${unit}]` : ''} <br/> ${o.status}`).join('<br/>')
                return `${date} ${labelEvents}<br /> ${result}`
            },
        },
        toolbox: getToolbox(),
        legend: getLegend(),
    }

    const echartRef = useRef()
    const timeoutRef = useRef()

    clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
        if (isNil(echartRef.current)) {
            return
        }
        const echartInstance = echartRef.current.getInstance()
        if (isNil(echartInstance)) {
            return
        }
        if ((!(tool === COMPENSATION) && !(tool === CORRECTION)) || isNull(startDate) || isNull(endDate) || isPreview) {
            echartInstance.setOption({
                graphic: {
                    type: 'rect',
                    position: [0, 0],
                    shape: {
                        x: 100,
                        y: 120,
                        width: 10000,
                        height: 550,
                    },
                    invisible: true,
                    z: 500,
                    onclick: selectOnChart,
                },
            })
            return
        }
        switch (tool) {
            case COMPENSATION:
                echartInstance.setOption({
                    graphic: {
                        id: 'group',
                        type: 'group',
                        draggable: true,
                        ondragstart: (e) => {
                            posClick.current = echartInstance.convertFromPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [e.offsetX, e.offsetY])
                        },
                        ondragend: (e) => {
                            const posRelease = echartInstance.convertFromPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [e.offsetX, e.offsetY])
                            const offset = [
                                posRelease[0] - posClick.current[0],
                                posRelease[1] - posClick.current[1],
                            ]
                            const toUpdate = serieLinePrevious.map(d => ({
                                ...d,
                                value: d.value + offset[1],
                            }))
                            setSerieLinePrevious(toUpdate)
                        },
                        children: [
                            {
                                id: 'clickLine',
                                type: 'polyline',
                                z: 100,
                                invisible: true,
                                shape: {
                                    points: serieLinePrevious.map(d => echartInstance.convertToPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [d.date, d.value])),
                                },
                                style: {
                                    stroke: '#00FF00',
                                    lineWidth: 20,
                                },
                            },
                            {
                                id: 'displayLine',
                                type: 'polyline',
                                origin: echartInstance.convertToPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [measuresSelectedByFilters[0].date, measuresSelectedByFilters[0].value]),
                                z: 100,
                                shape: {
                                    points: serieLinePrevious.map(d => echartInstance.convertToPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [d.date, d.value])),
                                },
                                style: {
                                    stroke: 'green',
                                    lineWidth: 2,
                                },
                            },
                            ...serieLinePrevious.map((d, i) => ({
                                id: `circle${i}`,
                                type: 'circle',
                                position: echartInstance.convertToPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [d.date, d.value]),
                                shape: {
                                    cx: 0,
                                    cy: 0,
                                    r: 3,
                                },
                                style: {
                                    stroke: 'green',
                                    fill: '#FFFFFF',
                                },
                                z: 101,
                            })),
                        ],
                    },
                })
                return
            case CORRECTION: {
                const point = measuresSelectedByFilters[measuresSelectedByFilters.length - 1]
                echartInstance.setOption({
                    graphic: {
                        id: 'group',
                        type: 'group',
                        draggable: true,
                        ondragstart: (e) => {
                            posClick.current = echartInstance.convertFromPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [e.offsetX, e.offsetY])
                        },
                        ondragend: (e) => {
                            const posRelease = echartInstance.convertFromPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [e.offsetX, e.offsetY])
                            const value = posRelease[1]
                            setValueCorrection(round(posRelease[1], 2))
                            const len = measuresSelectedByFilters?.length
                            const diff = value - measuresSelectedByFilters[len - 1]?.value
                            const lastDate = measuresSelectedByFilters[len - 1]?.date
                            const timeDiff = lastDate - measuresSelectedByFilters[0]?.date
                            const getDiff = (measure) => diff * (1 - ((lastDate - measure) / timeDiff))

                            const toUpdate = serieLinePrevious.map(d => ({
                                ...d,
                                value: d.value + getDiff(d.date),
                            }))
                            const findIndexStartDate = measuresFormatted.findIndex(m => m.date === startDate) - 1
                            const measureBeforeStartDate = measuresFormatted[findIndexStartDate]

                            const findIndexEndDate = measuresFormatted.findIndex(m => m.date === endDate) + 1
                            const measureAfterStartDate = measuresFormatted[findIndexEndDate]

                            const allMeasuresPreview = [
                                ...(measureBeforeStartDate ? [measureBeforeStartDate] : []),
                                ...toUpdate,
                                ...(measureAfterStartDate ? [measureAfterStartDate] : []),
                            ]
                            setAddLinePrevious(true)
                            setSerieLinePrevious(allMeasuresPreview)
                        },
                        children: [
                            {
                                id: 'clickLine',
                                type: 'polyline',
                                z: 100,
                                invisible: true,
                                shape: {
                                    points: echartInstance.convertToPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [point?.date, point?.value]),
                                },
                                style: {
                                    stroke: '#00FF00',
                                    lineWidth: 20,
                                },
                            },
                            {
                                id: 'displayLine',
                                type: 'polyline',
                                origin: echartInstance.convertToPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [serieLinePrevious[0]?.date, serieLinePrevious[0]?.value]),
                                z: 100,
                                shape: {
                                    points: echartInstance.convertToPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [point?.date, point?.value]),
                                },
                                style: {
                                    stroke: 'green',
                                    lineWidth: 2,
                                },
                            },
                            {
                                id: 'circle1',
                                type: 'circle',
                                position: echartInstance.convertToPixel({ xAxisIndex: 1, yAxisIndex: 2 }, [point?.date, point?.value]),
                                shape: {
                                    cx: 0,
                                    cy: 0,
                                    r: 3,
                                },
                                style: {
                                    stroke: 'green',
                                    fill: '#FFFFFF',
                                },
                                z: 101,
                            },
                        ],
                    },
                })
                return
            }
            default:
                echartInstance.setOption({
                    graphic: {
                        type: 'rect',
                        position: [0, 0],
                        shape: {
                            x: 100,
                            y: 120,
                            width: 10000,
                            height: 550,
                        },
                        invisible: true,
                        z: 500,
                        onclick: selectOnChart,
                    },
                })
        }
    }, 100)

    return dataLoaded ? (
        <EChart
            options={options}
            id='pluvioValidationChart'
            scrollable={false}
            ref={e => {
                if (!isNull(e)) {
                    echartRef.current = e
                }
            }}
        />
    ) : <div style={{ paddingTop: 55 }}><ProgressCard progress={50} /></div>
}

GraphicValidationPluvioMode.propTypes = {
    tempRef: PropTypes.number,
    selection: PropTypes.string,
    setStartDate: PropTypes.func,
    setEndDate: PropTypes.func,
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    pluvioMeasuresFormat: PropTypes.arrayOf(measurePluvioType),
    measuresSelectedByFilters: PropTypes.arrayOf(measurePluvioType),
    graphicOptions: PropTypes.shape({
        minY: PropTypes.number,
        maxY: PropTypes.number,
        intervalChoice: PropTypes.string,
        intervalNb: PropTypes.number,
        showXSplitLines: PropTypes.bool,
        showYSplitLines: PropTypes.bool,
    }),
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    dataType: PropTypes.number,
    pluvioDataTypes: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
    })),
    dataLoaded: PropTypes.bool,
    addLinePrevious: PropTypes.bool,
    isPreview: PropTypes.bool,
    isThresholShow: PropTypes.bool,
    tool: PropTypes.string,
    serieLinePrevious: PropTypes.arrayOf(measurePluvioType),
    setSerieLinePrevious: PropTypes.func,
    pluviometryThresholds: PropTypes.arrayOf(PropTypes.shape({})),
    contributors: PropTypes.arrayOf(PropTypes.shape({})),
    setAddLinePrevious: PropTypes.func,
    setValueCorrection: PropTypes.func,
    measuresChangesValidation: PropTypes.arrayOf(measurePluvioType),
    filterFullDay: PropTypes.shape({
        validationFilterFullDay: PropTypes.bool,
        setValidationFilterFullDay: PropTypes.func,
    }),
    showLegend: PropTypes.bool,
}

export default GraphicValidationPluvioMode
